import { Button, Modal } from '@loomhq/lens';
import React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { EDIT_REVERT_TO_ORIGINAL } from 'constants/events';
import { selectVideoId } from 'selectors/video';
import * as analytics from 'utilities/analytics';
import { getGraphQLClient } from 'utilities/graphql';

import { useClipsState } from './clips/state';
import { RevertToOriginal } from './graphql/RevertToOriginal.gql';
import { updateVideoPlayerSourceFromClip } from 'actions/videoPlayer';

const RevertToOriginalModal = (): JSX.Element => {
  const dispatch = useDispatch();
  const videoId = useSelector(selectVideoId);

  const { setClips, clearUndoStack, showRevertWarning, setShowRevertWarning } =
    useClipsState(state => ({
      setClips: state.setClips,
      clearUndoStack: state.clearUndoStack,
      showRevertWarning: state.showRevertWarning,
      setShowRevertWarning: state.setShowRevertWarning,
    }));

  const closeModal = () => setShowRevertWarning(false);

  const revertToOriginal = async () => {
    analytics.track(EDIT_REVERT_TO_ORIGINAL, {
      id: videoId,
      event_type: 'click',
    });
    const gqlClient = getGraphQLClient();

    const { data } = await gqlClient.mutate({
      mutation: RevertToOriginal,
      variables: {
        videoId,
      },
      fetchPolicy: 'no-cache',
    });

    if (
      data.revertToOriginal.__typename === 'RevertToOriginalPayload' &&
      data.revertToOriginal.video.clips.length !== 0
    ) {
      await setClips(data.revertToOriginal.video.clips);
      clearUndoStack();
      closeModal();

      const firstClip = data.revertToOriginal.video.clips.find(
        clip => clip?.position === 1 && clip?.source_url
      );

      if (firstClip) {
        dispatch(
          updateVideoPlayerSourceFromClip({
            sourceUrl: firstClip?.source_url,
            sourceMimeType: firstClip?.source_mime_type,
            partCredentials: firstClip?.source_part_credentials,
            sourceUrlIsTranscoded: firstClip?.source_url_is_transcoded,
            seekTime: 0,
          })
        );
      }
    }
  };

  return (
    <Modal
      id="revertToOriginalModal"
      title="Revert to original"
      isOpen={Boolean(showRevertWarning)}
      onCloseClick={closeModal}
      onBackgroundClick={closeModal}
      mainButton={
        <Button
          id="revertToOriginalDangerButton"
          variant="danger"
          onClick={revertToOriginal}
        >
          Revert to original
        </Button>
      }
      secondaryButton={
        <Button id="revertToOriginalCancelButton" onClick={closeModal}>
          Continue editing
        </Button>
      }
    >
      This will clear all changes you have made to your Loom and cannot be
      undone. Are you sure?
    </Modal>
  );
};

export default RevertToOriginalModal;
